import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Typography,
  Stack,
  Alert,
  Snackbar,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  SelectChangeEvent,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { MarketStore, Instance, Market, MarketCatalogue, MarketOrderType, MarketSupplier } from '../../app/services/api/orderManagementClient';
import { client } from '../../app/services/api/orderManagementClient';
import useLogError from '../../hooks/useLogError';

const StoreForm = () => {
  const { t } = useTranslation('stores');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { logError, extractErrorMessage } = useLogError();

  const isEditMode = Boolean(id);
  const [isLoading, setIsLoading] = useState<boolean>(isEditMode);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [formData, setFormData] = useState<MarketStore>({
    customerId: 0,
    companyId: 0,
    instanceName: '',
    customerTypeId: 0,
    marketName: '',
    erpLocationId: undefined,
    storeNumber: '',
    storeName: '',
    emailAddress: '',
    phoneNumber: '',
    shipToLocation: '',
    shipToAddress: '',
    town: '',
    county: '',
    country: '',
    postCode: '',
    inventoryEnabled: false,
    inventoryTestEnabled: false,
    inventoryStartDate: '',
    catalogueIds: [],
    odsConfiguration: [],
  });

  const [instances, setInstances] = useState<Instance[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [catalogues, setCatalogues] = useState<MarketCatalogue[]>([]);
  const [orderTypes, setOrderTypes] = useState<MarketOrderType[]>([]);
  const [marketSuppliers, setMarketSuppliers] = useState<MarketSupplier[]>([]);

  useEffect(() => {
    loadInstances();
    if (isEditMode && id) {
      loadStore(id);
    } else {
      setIsLoading(false);
    }
  }, [isEditMode, id]);

  useEffect(() => {
    if (formData.companyId > 0) {
      loadMarkets(formData.companyId.toString());
    }
  }, [formData.companyId]);

  useEffect(() => {
    if (formData.companyId > 0 && formData.customerTypeId > 0) {
      loadCatalogues(formData.companyId.toString());
    }
  }, [formData.companyId, formData.customerTypeId]);

  useEffect(() => {
    if (formData.companyId > 0 && formData.customerTypeId > 0) {
      loadOrderTypes(formData.companyId.toString(), formData.customerTypeId.toString());
      loadMarketSuppliers(formData.companyId.toString(), formData.customerTypeId.toString());
    }
  }, [formData.companyId, formData.customerTypeId]);

  const loadInstances = async () => {
    try {
      const response = await client.getInstances({
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (response.success && response.data) {
        setInstances(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load instances');
    }
  };

  const loadMarkets = async (instanceId: string) => {
    try {
      const response = await client.getMarkets({
        companyId: instanceId,
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (response.success && response.data) {
        setMarkets(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load markets');
    }
  };

  const loadCatalogues = async (instanceId: string) => {
    try {
      const response = await client.getMarketCatalogues({
        companyId: instanceId,
        customerTypeId: formData.customerTypeId.toString(),
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (response.success && response.data) {
        setCatalogues(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load catalogues');
    }
  };

  const loadOrderTypes = async (instanceId: string, marketId: string) => {
    try {
      const response = await client.getMarketOrderTypes({
        companyId: instanceId,
        customerTypeId: marketId,
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (response.success && response.data) {
        setOrderTypes(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load order types');
    }
  };

  const loadMarketSuppliers = async (instanceId: string, marketId: string) => {
    try {
      const response = await client.getMarketSuppliers({
        companyId: instanceId,
        customerTypeId: marketId,
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (response.success && response.data) {
        setMarketSuppliers(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load market suppliers');
    }
  };

  const loadStore = async (storeId: string) => {
    try {
      const response = await client.getMarketStore(storeId);
      
      if (response.success && response.data) {
        setFormData({
          ...response.data,
          catalogueIds: response.data.catalogueIds || [],
          inventoryStartDate: response.data.inventoryStartDate,
          companyId: response.data.companyId || 0,
          customerTypeId: response.data.customerTypeId || 0,
        });
      } else {
        setErrorMessage(response.message || t('loadErrorMessage'));
        setShowErrorMessage(true);
      }
    } catch (error) {
      logError('Failed to load store');
      setErrorMessage(t('loadErrorMessage'));
      setShowErrorMessage(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof MarketStore) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleNumberInputChange = (field: keyof MarketStore) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value === '' ? undefined : Number(event.target.value);
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSwitchChange = (field: keyof MarketStore) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const handleInstanceChange = (event: React.SyntheticEvent, value: Instance | null) => {
    setFormData(prev => ({
      ...prev,
      companyId: value?.id || 0,
      instanceName: value?.name || '',
      customerTypeId: 0,
      marketName: '',
      catalogueIds: [],
      odsConfiguration: [],
    }));
    
    setMarkets([]);
    setCatalogues([]);
    setMarketSuppliers([]);
    
    if (value?.id) {
      loadMarkets(value.id.toString());
    }
  };

  const handleMarketChange = (event: React.SyntheticEvent, value: Market | null) => {
    const marketId = value?.customerTypeId || 0;
    setFormData(prev => ({
      ...prev,
      customerTypeId: marketId,
      marketName: value?.name || '',
      catalogueIds: [],
      odsConfiguration: [],
    }));
    
    setCatalogues([]);
    setMarketSuppliers([]);
  };

  const handleDeliveryScheduleChange = (orderTypeId: number, deliveryDay: number, daysToCutoff: number) => {
    setFormData(prev => {
      const newOdsConfiguration = [...(prev.odsConfiguration || [])];
      const existingIndex = newOdsConfiguration.findIndex(
        ods => ods.orderTypeId === orderTypeId && ods.deliveryDay === deliveryDay,
      );

      if (existingIndex >= 0) {
        if (daysToCutoff === 0) {
          newOdsConfiguration.splice(existingIndex, 1);
        } else {
          newOdsConfiguration[existingIndex] = {
            ...newOdsConfiguration[existingIndex],
            daysToCutoff: daysToCutoff.toString(),
          };
        }
      } else if (daysToCutoff > 0) {
        newOdsConfiguration.push({
          deliveryScheduleRuleId: undefined,
          orderTypeId: orderTypeId,
          orderType: orderTypes.find(ot => ot.orderTypeId === orderTypeId)?.name || '',
          week: 0,
          deliveryDay: deliveryDay,
          daysToCutoff: daysToCutoff.toString(),
          supplierNumber: '',
          supplierName: '',
        });
      }

      return {
        ...prev,
        odsConfiguration: newOdsConfiguration,
      };
    });
  };

  const handleSupplierDeliveryScheduleChange = (
    orderTypeId: number,
    deliveryDay: number,
    daysToCutoff: number,
    supplierName?: string | null,
  ) => {
    setFormData(prev => {
      const newOdsConfiguration = [...(prev.odsConfiguration || [])];
      const existingIndex = newOdsConfiguration.findIndex(
        ods => ods.orderTypeId === orderTypeId && 
               ods.deliveryDay === deliveryDay &&
               ods.supplierName === supplierName,
      );

      if (existingIndex >= 0) {
        if (daysToCutoff === 0) {
          newOdsConfiguration.splice(existingIndex, 1);
        } else {
          newOdsConfiguration[existingIndex] = {
            ...newOdsConfiguration[existingIndex],
            daysToCutoff: daysToCutoff.toString(),
          };
        }
      } else if (daysToCutoff > 0) {
        const existingConfig = newOdsConfiguration.find(
          config => config.orderTypeId === orderTypeId && config.supplierName === supplierName,
        );
        
        newOdsConfiguration.push({
          deliveryScheduleRuleId: undefined,
          orderTypeId: orderTypeId,
          orderType: orderTypes.find(ot => ot.orderTypeId === orderTypeId)?.name || '',
          week: existingConfig?.week || 1,
          deliveryDay: deliveryDay,
          daysToCutoff: daysToCutoff.toString(),
          supplierNumber: existingConfig?.supplierNumber || '',
          supplierName: supplierName || '',
        });
      }

      return {
        ...prev,
        odsConfiguration: newOdsConfiguration,
      };
    });
  };

  const handleCatalogueToggle = (catalogueId: number) => {
    setFormData(prev => ({
      ...prev,
      catalogueIds: prev.catalogueIds.includes(catalogueId)
        ? prev.catalogueIds.filter(id => id !== catalogueId)
        : [...prev.catalogueIds, catalogueId],
    }));
  };

  const handleCancel = () => {
    navigate('/stores');
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);

    try {
      let response;
      
      if (isEditMode && id) {
        response = await client.updateMarketStore(id, formData);
      } else {
        response = await client.createMarketStore(formData);
      }

      if (response.success) {
        setShowSuccessMessage(true);
        setTimeout(() => {
          navigate('/stores');
        }, 500);
      } else if (response.message) {
        setErrorMessage(response.message);
        setShowErrorMessage(true);
      }
    } catch (error) {
      logError(isEditMode ? 'Failed to update store' : 'Failed to create store');
      const errorMessage = extractErrorMessage(error);
      setErrorMessage(errorMessage || (isEditMode ? t('updateErrorMessage') : t('createErrorMessage')));
      setShowErrorMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  const renderDeliveryScheduleTable = () => {
    const days = [
      { key: 1, label: t('day1'), fullName: 'Monday' },
      { key: 2, label: t('day2'), fullName: 'Tuesday' },
      { key: 3, label: t('day3'), fullName: 'Wednesday' },
      { key: 4, label: t('day4'), fullName: 'Thursday' },
      { key: 5, label: t('day5'), fullName: 'Friday' },
      { key: 6, label: t('day6'), fullName: 'Saturday' },
      { key: 7, label: t('day7'), fullName: 'Sunday' },
    ];

    const getCutoffOptions = (deliveryDay: number) => {
      const options = [{ value: 0, label: 'No Delivery' }];
      for (let i = 1; i <= 6; i++) {
        let cutoffDay = deliveryDay - i;
        if (cutoffDay <= 0) {
          cutoffDay += 7;
        }     
        const dayNames = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        options.push({
          value: i,
          label: `Cut off ${dayNames[cutoffDay]}`,
        });
      }
      return options;
    };

    const getOrderTypeDisplay = (orderType: MarketOrderType) => {

      if (orderType.orderTypeId === 12) {
        return marketSuppliers.map(supplier => ({
          ...orderType,
          displayName: orderType.name,
          supplierName: supplier.supplierName,
          isSupplierSpecific: true,
        }));
      }

      return [{
        ...orderType,
        displayName: orderType.name,
        supplierName: '',
        isSupplierSpecific: false,
      }];
    };

    const displayOrderTypes = orderTypes.flatMap(getOrderTypeDisplay);

    const findConfiguration = (orderTypeId: number, deliveryDay: number, supplierName?: string | null) => {
      return (formData.odsConfiguration || []).find(
        ods => ods.orderTypeId === orderTypeId && 
               ods.deliveryDay === deliveryDay &&
               (supplierName ? ods.supplierName === supplierName : !ods.supplierName),
      );
    };

    return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('orderType')}</TableCell>
              {days.map(day => (
                <TableCell
                  key={day.key}
                  align="center"
                  sx={{ minWidth: 150 }}
                >
                  {day.fullName} Delivery
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayOrderTypes.map((orderType, index) => (
              <TableRow key={`${orderType.orderTypeId}-${orderType.supplierName || 'default'}-${index}`}>
                <TableCell>
                  {orderType.displayName}
                  {orderType.isSupplierSpecific && (
                    <Typography 
                      sx={{ fontSize: '0.75rem' }}
                      display="block" 
                      color="text.secondary"
                    >
                      {orderType.supplierName}
                    </Typography>
                  )}
                </TableCell>
                {days.map(day => {
                  const config = findConfiguration(orderType.orderTypeId, day.key, orderType.supplierName);
                  const currentValue = config ? Number(config.daysToCutoff) : 0;

                  return (
                    <TableCell
                      key={day.key}
                      align="center"
                    >
                      <FormControl 
                        fullWidth 
                        size="small"
                      >
                        <Select
                          value={currentValue}
                          onChange={(event: SelectChangeEvent<number>) => {
                            const newValue = Number(event.target.value);
                            if (orderType.isSupplierSpecific) {
                              handleSupplierDeliveryScheduleChange(
                                orderType.orderTypeId, 
                                day.key, 
                                newValue,
                                orderType.supplierName,
                              );
                            } else {
                              handleDeliveryScheduleChange(
                                orderType.orderTypeId, 
                                day.key, 
                                newValue,
                              );
                            }
                          }}
                          displayEmpty
                        >
                          {getCutoffOptions(day.key).map(option => (
                            <MenuItem 
                              key={option.value} 
                              value={option.value}
                            >
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (isLoading) {
    return (
      <PageArea>
        <PageTitle>{t(isEditMode ? 'management.editStore' : 'management.createStore')}</PageTitle>
        <Typography>{t('management.loading')}</Typography>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Stack spacing={8}>
        {/* Basic Store Information Card */}
        <Card elevation={1}>
          <CardHeader
            title={t('management.basicInformation')}
            sx={{
              color: 'text.primary',
            }}
          />
          <CardContent
            sx={{
              p: 8,
            }}
          >
            <Grid
              container
              spacing={8}
            >
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('storeName')}
                  value={formData.storeName}
                  onChange={handleInputChange('storeName')}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('storeNumber')}
                  value={formData.storeNumber}
                  onChange={handleInputChange('storeNumber')}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('emailAddress')}
                  type="email"
                  value={formData.emailAddress}
                  onChange={handleInputChange('emailAddress')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('phoneNumber')}
                  value={formData.phoneNumber}
                  onChange={handleInputChange('phoneNumber')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('erpLocationId')}
                  type="number"
                  value={formData.erpLocationId || ''}
                  onChange={handleNumberInputChange('erpLocationId')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Location Details Card */}
        <Card elevation={1}>
          <CardHeader
            title={t('management.locationDetails')}
            sx={{
              color: 'text.primary',
            }}
          />
          <CardContent
            sx={{
              p: 8,
            }}
          >
            <Grid
              container
              spacing={8}
            >
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('shipToLocation')}
                  value={formData.shipToLocation}
                  onChange={handleInputChange('shipToLocation')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('shipToAddress')}
                  value={formData.shipToAddress}
                  onChange={handleInputChange('shipToAddress')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('town')}
                  value={formData.town}
                  onChange={handleInputChange('town')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('county')}
                  value={formData.county}
                  onChange={handleInputChange('county')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('country')}
                  value={formData.country}
                  onChange={handleInputChange('country')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('postCode')}
                  value={formData.postCode}
                  onChange={handleInputChange('postCode')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Instance and Market Configuration Card */}
        <Card elevation={1}>
          <CardHeader
            title={t('configuration')}
            sx={{
              color: 'text.primary',
            }}
          />
          <CardContent
            sx={{
              p: 8,
            }}
          >
            <Grid
              container
              spacing={8}
            >
              <Grid
                item
                xs={12}
                md={6}
              >
                <Autocomplete
                  options={instances}
                  getOptionLabel={(option) => option.name}
                  value={instances.find(instance => instance.id === formData.companyId) || null}
                  onChange={handleInstanceChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('instance')}
                      required
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <Autocomplete
                  options={markets}
                  getOptionLabel={(option) => option.name}
                  value={markets.find(market => market.customerTypeId === formData.customerTypeId) || null}
                  onChange={handleMarketChange}
                  disabled={!formData.companyId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('market')}
                      required
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('inventoryStartDate')}
                  type="date"
                  value={formData.inventoryStartDate || ''}
                  onChange={handleInputChange('inventoryStartDate')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
              >
                <Grid
                  container
                  spacing={4}
                >
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.inventoryEnabled || false}
                          onChange={handleSwitchChange('inventoryEnabled')}
                        />
                      }
                      label={
                        <Typography
                          variant="textMD"
                          sx={{
                            ml: 1,
                          }}
                        >
                          {t('inventoryEnabled')}
                        </Typography>
                      }
                      sx={{
                        m: 0,
                        p: 2,
                        backgroundColor: 'background.paper',
                        alignItems: 'center',
                      }}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.inventoryTestEnabled || false}
                          onChange={handleSwitchChange('inventoryTestEnabled')}
                        />
                      }
                      label={
                        <Typography
                          variant="textMD"
                          sx={{
                            ml: 1,
                          }}
                        >
                          {t('inventoryTestEnabled')}
                        </Typography>
                      }
                      sx={{
                        m: 0,
                        p: 2,
                        backgroundColor: 'background.paper',
                        alignItems: 'center',
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Catalogue Configuration Card */}
        {formData.companyId > 0 && formData.customerTypeId > 0 && (
          <Card elevation={1}>
            <CardHeader
              title={t('management.catalogueConfiguration')}
              sx={{
                color: 'text.primary',
              }}
            />
            <CardContent
              sx={{
                p: 8,
              }}
            >
              <Typography
                sx={{
                  mb: 4,
                }}
              >
                {t('selectCatalogues')}
              </Typography>
              <Grid 
                container 
                spacing={2}
              >
                {catalogues.map((catalogue) => (
                  <Grid 
                    item 
                    xs={12} 
                    sm={6} 
                    md={4} 
                    key={catalogue.customerGroupId}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.catalogueIds.includes(catalogue.customerGroupId)}
                          onChange={() => handleCatalogueToggle(catalogue.customerGroupId)}
                        />
                      }
                      label={catalogue.name}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Delivery Schedule Configuration Card */}
        {formData.companyId > 0 && formData.customerTypeId > 0 && orderTypes.length > 0 && (
          <Card elevation={1}>
            <CardHeader
              title={t('management.deliveryScheduleConfiguration')}
              sx={{
                color: 'text.primary',
              }}
            />
            <CardContent
              sx={{
                p: 8,
              }}
            >
              <Typography
                sx={{
                  mb: 4,
                }}
              >
                {t('orderDeliverySchedule')}
              </Typography>
              {renderDeliveryScheduleTable()}
            </CardContent>
          </Card>
        )}

        {/* Action Buttons Card */}
        <Card
          elevation={0}
          sx={{
            backgroundColor: 'background.default',
          }}
        >
          <CardContent
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 4,
              p: 8,
            }}
          >
            <Button
              variant="secondary"
              size="lg"
              onClick={handleCancel}
              disabled={isSaving}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={handleSubmit}
              disabled={isSaving || !formData.storeName || !formData.storeNumber || !formData.companyId || !formData.customerTypeId}
            >
              {isSaving ? t('saving') : (isEditMode ? t('update') : t('create'))}
            </Button>
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        open={showSuccessMessage}
        autoHideDuration={5000}
        onClose={() => setShowSuccessMessage(false)}
      >
        <Alert
          onClose={() => setShowSuccessMessage(false)}
          severity="success"
          variant="filled"
          sx={{
            width: '100%',
          }}
        >
          {isEditMode ? t('updateSuccessMessage') : t('createSuccessMessage')}
        </Alert>
      </Snackbar>

      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        open={showErrorMessage}
        autoHideDuration={5000}
        onClose={() => setShowErrorMessage(false)}
      >
        <Alert
          onClose={() => setShowErrorMessage(false)}
          severity="error"
          variant="filled"
          sx={{
            width: '100%',
          }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default StoreForm;
